import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { GoogleLogin } from "@react-oauth/google";
import { useAuth } from "@/lib/auth";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function LoginPage() {
  const { user, loading, login, googleLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleGoogle(credential: string | undefined) {
    setError("");
    if (!credential) {
      setError("Google sign-in failed");
      return;
    }
    setSubmitting(true);
    try {
      await googleLogin(credential);
      navigate({ to: "/dpps" });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      setError(
        status === 401
          ? "This Google account isn't an authorized admin. Ask a superadmin for an invite."
          : "Google sign-in failed",
      );
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(() => {
    if (!loading && user) navigate({ to: "/dpps" });
  }, [loading, user, navigate]);

  async function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate({ to: "/dpps" });
    } catch (err: unknown) {
      setError(apiErrorMessage(err, "Sign in failed"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="admlogin"
      style={{
        minHeight: "100vh",
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        position: "relative",
        zIndex: 1,
      }}
    >
      <aside
        style={{
          background: "var(--bg)",
          padding: "48px 56px 48px 48px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-end",
          borderRight: "1px solid var(--line)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `
            linear-gradient(rgba(217,120,104,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(217,120,104,0.06) 1px, transparent 1px)
          `,
            backgroundSize: "48px 48px",
            pointerEvents: "none",
          }}
        />

        <div style={{ position: "relative", zIndex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              marginBottom: 20,
            }}
          >
            <span
              style={{
                width: 36,
                height: 36,
                border: "1px solid var(--ink)",
                position: "relative",
                display: "inline-block",
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width: 10,
                  height: 10,
                  background: "var(--accent)",
                  transform: "translate(-50%, -50%)",
                  display: "block",
                }}
              />
            </span>
            <span
              style={{
                fontFamily: "var(--f-mono)",
                fontSize: 11,
                letterSpacing: "0.22em",
                color: "var(--accent)",
                border: "1px solid var(--accent)",
                padding: "3px 8px",
                textTransform: "uppercase",
              }}
            >
              Admin
            </span>
          </div>
          <h1
            style={{
              fontFamily: "var(--f-head)",
              fontWeight: 500,
              fontSize: "clamp(32px, 3.5vw, 48px)",
              lineHeight: 1.05,
              letterSpacing: "-0.015em",
              margin: 0,
              color: "var(--ink)",
            }}
          >
            Provenance
          </h1>
        </div>
      </aside>

      <main
        style={{
          padding: "64px 64px 64px 56px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          maxWidth: 520,
        }}
      >
        <h2
          style={{
            fontFamily: "var(--f-head)",
            fontWeight: 500,
            fontSize: 32,
            margin: "0 0 24px",
          }}
        >
          Sign in
        </h2>

        <form className="form" onSubmit={handleSubmit} autoComplete="on">
          <div className="field">
            <Label htmlFor="em">Operator email</Label>
            <Input
              id="em"
              type="email"
              placeholder="you@watchcentral.com"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div className="field">
            <Label htmlFor="pw">Password</Label>
            <Input
              id="pw"
              type="password"
              placeholder="••••••••••••"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />
          </div>

          {error && (
            <div
              style={{
                fontFamily: "var(--f-mono)",
                fontSize: 12,
                color: "var(--danger)",
                border: "1px solid var(--danger)",
                padding: "10px 14px",
                letterSpacing: "0.06em",
              }}
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            style={{ width: "100%", opacity: submitting ? 0.6 : 1 }}
            disabled={submitting}
            size="lg"
          >
            {submitting ? "Signing in…" : "Sign in to console"}
            {!submitting && (
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M3 7h8M7 3l4 4-4 4" />
              </svg>
            )}
          </Button>
        </form>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            margin: "24px 0 20px",
          }}
        >
          <span style={{ flex: 1, height: 1, background: "var(--line)" }} />
          <span
            style={{
              fontFamily: "var(--f-mono)",
              fontSize: 10,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "var(--ink-mute)",
            }}
          >
            or
          </span>
          <span style={{ flex: 1, height: 1, background: "var(--line)" }} />
        </div>

        <GoogleLogin
          theme="outline"
          text="signin_with"
          shape="rectangular"
          width="320"
          onSuccess={(resp) => handleGoogle(resp.credential)}
          onError={() => setError("Google sign-in failed")}
        />
      </main>
    </div>
  );
}
